// The canvas. Its container units size everything on it, so the original always fits, even turned
// on its side, and the picture is the same on a phone. The parent sets `--stage-min` to change how
// tall it is.
.stage {
  // The original's longer side fits the stage whichever way it is turned.
  --unit: min(calc(72cqw / var(--original-long)), calc(78cqh / var(--original-long)));
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 0.75rem;
  padding-block: 0.875rem;
  box-sizing: border-box;
  // A definite height, so the container units that size the picture have one to measure even where
  // nothing else gives the stage a height (a dialog); in a taller row it grows to fill it.
  flex: 1 1 auto;
  height: var(--stage-min, 24rem);
  min-height: var(--stage-min, 24rem);
  place-items: center;
  overflow: hidden;
  container-type: size;
  background-image: radial-gradient(var(--color-border) 1px, transparent 1px);
  background-size: 1rem 1rem;
}

.canvas {
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  min-height: 0;
  container-type: size;
  overflow: hidden;
}

// Where the original sits, for the picture to shrink away from.
.ghost {
  position: absolute;
  width: calc(var(--original-w) * var(--unit));
  height: calc(var(--original-h) * var(--unit));
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-subtle);

  // The original behind the picture, faded, so what the transform kept or took away shows.
  .scene {
    opacity: 0.28;
  }
}

// The picture after the phase the loop is at; changing it is what animates.
.frame {
  position: relative;
  grid-area: 1 / 1;
  width: calc(var(--box-w) * var(--unit));
  height: calc(var(--box-h) * var(--unit));
  overflow: hidden;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-lg);
  transition:
    width 800ms cubic-bezier(0.77, 0, 0.175, 1),
    height 800ms cubic-bezier(0.77, 0, 0.175, 1),
    rotate 800ms cubic-bezier(0.77, 0, 0.175, 1),
    scale 800ms cubic-bezier(0.77, 0, 0.175, 1),
    filter 800ms cubic-bezier(0.77, 0, 0.175, 1);
}

// Whatever the picture is (an `img` or an svg) fills the box it is drawn in, cropping to cover it.
.scene {
  width: 100%;
  height: 100%;

  > * {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// A conversion changes no pixels you can see: a sweep across the picture shows it being re-encoded.
.frame[data-converting]::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 35%, rgb(255 255 255 / 0.45) 50%, transparent 65%);
  animation: sweep 900ms ease-out both;
}

@keyframes sweep {
  from {
    opacity: 1;
    translate: -100% 0;
  }

  to {
    opacity: 0;
    translate: 100% 0;
  }
}

.dimensions {
  margin: 0;
  text-align: center;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  color: var(--color-foreground);
}

.badges {
  position: absolute;
  top: 0.75rem;
  inset-inline-end: 0.75rem;
  display: flex;
  gap: 0.375rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-card);
  font-size: 0.75rem;
  font-weight: 500;

  svg {
    width: 0.875rem;
    height: 0.875rem;
    color: var(--color-primary);
  }
}

@media (prefers-reduced-motion: reduce) {
  .frame {
    transition: none;
  }

  .frame[data-converting]::after {
    animation: none;
  }
}
